import type { NodeMapping, TransformParamResult } from './types';

/**
 * Mapping from n8n node types to EWA node types
 *
 * Nodes not in this map will become no-op placeholders and logged as gaps.
 *
 * Based on analysis of 45MB n8n workflow export with ~140 unique node types.
 */
export const N8N_TO_EWA_NODE_MAP: Record<string, NodeMapping> = {
  // =========================================================================
  // TRIGGERS
  // =========================================================================
  'n8n-nodes-base.scheduleTrigger': { ewaType: 'schedule-trigger', parameterTransform: transformScheduleParams },
  'n8n-nodes-base.manualTrigger': { ewaType: 'manual-trigger' },
  'n8n-nodes-base.webhook': { ewaType: 'webhook-trigger', parameterTransform: transformWebhookParams },
  'n8n-nodes-base.errorTrigger': { ewaType: 'error-trigger' },
  'n8n-nodes-base.chatTrigger': { ewaType: 'chat-trigger' },
  '@n8n/n8n-nodes-langchain.chatTrigger': { ewaType: 'chat-trigger' },
  '@n8n/n8n-nodes-langchain.manualChatTrigger': { ewaType: 'chat-trigger', notes: 'Manual chat trigger' },
  'n8n-nodes-base.emailTrigger': { ewaType: 'polling-trigger', notes: 'Maps to polling trigger' },
  'n8n-nodes-base.executeWorkflowTrigger': { ewaType: 'execute-workflow', notes: 'Trigger for sub-workflows' },
  'n8n-nodes-base.formTrigger': { ewaType: 'webhook-trigger', parameterTransform: transformFormTriggerParams, notes: 'Form submissions handled via webhook' },
  'n8n-nodes-base.form': { ewaType: 'no-op', notes: 'Form step/page - configure webhook response instead' },
  'n8n-nodes-base.cron': { ewaType: 'schedule-trigger', parameterTransform: transformCronParams, notes: 'Legacy cron trigger' },
  'n8n-nodes-base.start': { ewaType: 'manual-trigger', notes: 'Legacy start node' },
  'n8n-nodes-base.n8nTrigger': { ewaType: 'webhook-trigger', notes: 'n8n event trigger - use webhook' },
  'n8n-nodes-base.localFileTrigger': { ewaType: 'polling-trigger', notes: 'Local file watcher - use polling' },
  'n8n-nodes-base.evaluationTrigger': { ewaType: 'manual-trigger', notes: 'AI evaluation trigger' },

  // Microsoft Triggers
  'n8n-nodes-base.microsoftOutlookTrigger': { ewaType: 'microsoft-outlook-trigger' },
  'n8n-nodes-base.microsoftTeamsTrigger': { ewaType: 'polling-trigger', parameterTransform: transformMicrosoftTeamsTriggerParams, notes: 'Teams trigger - use polling with Teams API' },
  'n8n-nodes-base.microsoftOneDriveTrigger': { ewaType: 'polling-trigger', notes: 'OneDrive trigger - use polling' },

  // Third-party Triggers (coming soon in EWA)
  'n8n-nodes-base.gmailTrigger': { ewaType: 'polling-trigger', notes: 'Gmail trigger - use polling (Gmail coming soon)' },
  'n8n-nodes-base.githubTrigger': { ewaType: 'webhook-trigger', notes: 'GitHub trigger - use webhook (GitHub coming soon)' },
  'n8n-nodes-base.jiraTrigger': { ewaType: 'polling-trigger', notes: 'Jira trigger - use polling (Jira coming soon)' },
  'n8n-nodes-base.asanaTrigger': { ewaType: 'polling-trigger', notes: 'Asana trigger - use polling (Asana coming soon)' },
  'n8n-nodes-base.telegramTrigger': { ewaType: 'webhook-trigger', notes: 'Telegram trigger - use webhook' },
  'n8n-nodes-base.typeformTrigger': { ewaType: 'webhook-trigger', notes: 'Typeform trigger - use webhook' },

  // MCP Trigger
  '@n8n/n8n-nodes-langchain.mcpTrigger': { ewaType: 'mcp-trigger' },

  // =========================================================================
  // FLOW CONTROL
  // =========================================================================
  'n8n-nodes-base.if': { ewaType: 'if', parameterTransform: transformIfParams },
  'n8n-nodes-base.switch': { ewaType: 'switch', parameterTransform: transformSwitchParams },
  'n8n-nodes-base.merge': { ewaType: 'merge', parameterTransform: transformMergeParams },
  'n8n-nodes-base.splitInBatches': { ewaType: 'split-in-batches', parameterTransform: transformSplitInBatchesParams },
  'n8n-nodes-base.executeWorkflow': { ewaType: 'execute-workflow-action', parameterTransform: transformExecuteWorkflowParams },
  'n8n-nodes-base.wait': { ewaType: 'wait', parameterTransform: transformWaitParams },
  'n8n-nodes-base.noOp': { ewaType: 'no-op' },
  'n8n-nodes-base.filter': { ewaType: 'filter', parameterTransform: transformFilterParams },
  'n8n-nodes-base.limit': { ewaType: 'limit', parameterTransform: transformLimitParams },
  'n8n-nodes-base.loop': { ewaType: 'loop' },
  'n8n-nodes-base.stopAndError': { ewaType: 'no-op', parameterTransform: transformStopAndErrorParams, notes: 'Stop and error - creates no-op with error info' },

  // =========================================================================
  // HTTP / API
  // =========================================================================
  'n8n-nodes-base.httpRequest': { ewaType: 'http-request', parameterTransform: transformHttpRequestParams },
  'n8n-nodes-base.respondToWebhook': { ewaType: 'respond-to-trigger', parameterTransform: transformRespondToWebhookParams },
  'n8n-nodes-base.graphql': { ewaType: 'graphql', parameterTransform: transformGraphqlParams },
  'n8n-nodes-base.postBin': { ewaType: 'http-request', notes: 'PostBin - use HTTP request' },

  // =========================================================================
  // DATABASES
  // =========================================================================
  'n8n-nodes-base.microsoftSql': { ewaType: 'MsSQL', parameterTransform: transformSqlParams },
  'n8n-nodes-base.microsoftSqlTool': { ewaType: 'MsSQL', parameterTransform: transformSqlParams, notes: 'SQL tool for AI agents' },
  'n8n-nodes-base.postgres': { ewaType: 'postgresql', parameterTransform: transformPostgresParams },
  'n8n-nodes-base.postgresTool': { ewaType: 'postgresql', parameterTransform: transformPostgresParams, notes: 'Postgres tool for AI agents' },
  'n8n-nodes-base.mySql': { ewaType: 'mysql', parameterTransform: transformMysqlParams },
  'n8n-nodes-base.mongoDb': { ewaType: 'mongodb', parameterTransform: transformMongoDbParams },
  'n8n-nodes-base.redis': { ewaType: 'redis', parameterTransform: transformRedisParams },
  'n8n-nodes-base.turso': { ewaType: 'turso' },
  'n8n-nodes-base.dataTable': { ewaType: 'data-table' },
  'n8n-nodes-base.dataTableTool': { ewaType: 'data-table', notes: 'Data table tool for AI agents' },
  'n8n-nodes-base.snowflake': { ewaType: 'MsSQL', parameterTransform: transformSnowflakeParams, notes: 'Snowflake - maps to SQL with snowflake dialect' },
  'n8n-nodes-base.snowflakeTool': { ewaType: 'MsSQL', parameterTransform: transformSnowflakeParams, notes: 'Snowflake tool for AI agents' },
  'n8n-nodes-base.supabase': { ewaType: 'postgresql', notes: 'Supabase - use PostgreSQL' },
  'n8n-nodes-base.airtable': { ewaType: 'no-op', notes: 'Airtable - coming soon in EWA' },

  // =========================================================================
  // DATA TRANSFORMATION
  // =========================================================================
  'n8n-nodes-base.code': { ewaType: 'function', parameterTransform: transformCodeParams },
  'n8n-nodes-base.function': { ewaType: 'function', parameterTransform: transformFunctionParams },
  'n8n-nodes-base.functionItem': { ewaType: 'function', parameterTransform: transformFunctionItemParams },
  'n8n-nodes-base.set': { ewaType: 'edit-fields', parameterTransform: transformSetParams },
  'n8n-nodes-base.aggregate': { ewaType: 'aggregate', parameterTransform: transformAggregateParams },
  'n8n-nodes-base.sort': { ewaType: 'sort', parameterTransform: transformSortParams },
  'n8n-nodes-base.removeDuplicates': { ewaType: 'remove-duplicates', parameterTransform: transformRemoveDuplicatesParams },
  'n8n-nodes-base.dateTime': { ewaType: 'datetime', parameterTransform: transformDateTimeParams },
  'n8n-nodes-base.dateTimeTool': { ewaType: 'datetime', notes: 'DateTime tool for AI agents' },
  'n8n-nodes-base.crypto': { ewaType: 'crypto', parameterTransform: transformCryptoParams },
  'n8n-nodes-base.xml': { ewaType: 'xml', parameterTransform: transformXmlParams },
  'n8n-nodes-base.html': { ewaType: 'html-extract', parameterTransform: transformHtmlParams },
  'n8n-nodes-base.htmlExtract': { ewaType: 'html-extract', parameterTransform: transformHtmlExtractParams },
  'n8n-nodes-base.markdown': { ewaType: 'convert-to-html' },
  'n8n-nodes-base.compareDatasets': { ewaType: 'compare-datasets', parameterTransform: transformCompareDataParams },
  'n8n-nodes-base.itemLists': { ewaType: 'transform', parameterTransform: transformItemListsParams },
  'n8n-nodes-base.splitOut': { ewaType: 'transform', parameterTransform: transformSplitOutParams },
  'n8n-nodes-base.spreadsheetFile': { ewaType: 'csv-to-json', parameterTransform: transformSpreadsheetParams, notes: 'CSV operations' },
  'n8n-nodes-base.json': { ewaType: 'json' },
  'n8n-nodes-base.csvToJson': { ewaType: 'csv-to-json' },
  'n8n-nodes-base.jsonToCsv': { ewaType: 'json-to-csv' },
  'n8n-nodes-base.extractFromFile': { ewaType: 'binary-to-text', parameterTransform: transformExtractFromFileParams },
  'n8n-nodes-base.convertToFile': { ewaType: 'json-to-csv', parameterTransform: transformConvertToFileParams, notes: 'Convert to file' },
  'n8n-nodes-base.renameKeys': { ewaType: 'edit-fields', notes: 'Rename keys - use edit fields' },
  'n8n-nodes-base.summarize': { ewaType: 'aggregate', notes: 'Summarize - use aggregate' },
  'n8n-nodes-base.aiTransform': { ewaType: 'ai-agent', notes: 'AI Transform - use AI agent' },

  // File Operations
  'n8n-nodes-base.readBinaryFile': { ewaType: 'binary-to-text', notes: 'Read binary file' },
  'n8n-nodes-base.readBinaryFiles': { ewaType: 'binary-to-text', notes: 'Read binary files' },
  'n8n-nodes-base.writeBinaryFile': { ewaType: 'no-op', notes: 'Write binary file - use FTP or cloud storage' },
  'n8n-nodes-base.readWriteFile': { ewaType: 'no-op', notes: 'Read/write file - use FTP or cloud storage' },
  'n8n-nodes-base.localFile': { ewaType: 'no-op', notes: 'Local file - use FTP or cloud storage' },
  'n8n-nodes-base.compression': { ewaType: 'no-op', notes: 'Compression - handle in code node' },
  'n8n-nodes-base.editImage': { ewaType: 'no-op', notes: 'Edit image - not yet supported' },

  // =========================================================================
  // AI / LLM NODES
  // =========================================================================
  '@n8n/n8n-nodes-langchain.agent': { ewaType: 'ai-agent', parameterTransform: transformAgentParams },
  '@n8n/n8n-nodes-langchain.lmChatOpenAi': { ewaType: 'openai', parameterTransform: transformLmChatOpenAiParams },
  '@n8n/n8n-nodes-langchain.lmChatAnthropic': { ewaType: 'anthropic', parameterTransform: transformLmChatAnthropicParams },
  '@n8n/n8n-nodes-langchain.lmChatAzureOpenAi': { ewaType: 'azure-openai', parameterTransform: transformLmChatAzureOpenAiParams },
  '@n8n/n8n-nodes-langchain.lmChatGoogleGemini': { ewaType: 'openai', parameterTransform: transformLmChatGoogleGeminiParams, notes: 'Google Gemini - map to OpenAI compatible' },
  '@n8n/n8n-nodes-langchain.lmChatOllama': { ewaType: 'openai', notes: 'Ollama - map to OpenAI compatible endpoint' },
  '@n8n/n8n-nodes-langchain.lmChatOpenRouter': { ewaType: 'openai', notes: 'OpenRouter - map to OpenAI compatible' },
  '@n8n/n8n-nodes-langchain.memoryBufferWindow': { ewaType: 'simpleMemory', parameterTransform: transformMemoryBufferParams },
  '@n8n/n8n-nodes-langchain.memoryManager': { ewaType: 'chat-memory-manager' },
  '@n8n/n8n-nodes-langchain.chainSummarization': { ewaType: 'summarise' },
  '@n8n/n8n-nodes-langchain.chainLlm': { ewaType: 'ai-agent', parameterTransform: transformChainLlmParams, notes: 'LLM Chain - use AI agent' },
  '@n8n/n8n-nodes-langchain.chainRetrievalQa': { ewaType: 'ai-agent', notes: 'Retrieval QA chain - use AI agent with KB' },
  '@n8n/n8n-nodes-langchain.toolWorkflow': { ewaType: 'execute-workflow-tool' },
  '@n8n/n8n-nodes-langchain.outputParserStructured': { ewaType: 'agentOutputParser', parameterTransform: transformOutputParserParams },
  '@n8n/n8n-nodes-langchain.mcpClientTool': { ewaType: 'mcp-tool' },
  'n8n-nodes-base.openAi': { ewaType: 'openai', parameterTransform: transformOpenAiParams },
  '@n8n/n8n-nodes-langchain.openAi': { ewaType: 'openai' },
  '@n8n/n8n-nodes-langchain.googleGemini': { ewaType: 'openai', notes: 'Google Gemini - map to OpenAI compatible' },
  '@n8n/n8n-nodes-langchain.anthropic': { ewaType: 'anthropic' },
  '@n8n/n8n-nodes-langchain.anthropicTool': { ewaType: 'anthropic', notes: 'Anthropic tool for AI agents' },
  '@n8n/n8n-nodes-langchain.toolCode': { ewaType: 'function', parameterTransform: transformToolCodeParams, notes: 'Code tool for AI agents' },
  '@n8n/n8n-nodes-langchain.chat': { ewaType: 'ai-agent', notes: 'Basic chat - use AI agent' },
  '@n8n/n8n-nodes-langchain.informationExtractor': { ewaType: 'ai-agent', parameterTransform: transformInformationExtractorParams, notes: 'Information extraction - configure agent' },
  '@n8n/n8n-nodes-langchain.textClassifier': { ewaType: 'ai-agent', notes: 'Text classifier - use AI agent' },
  '@n8n/n8n-nodes-langchain.agentTool': { ewaType: 'ai-agent', parameterTransform: transformAgentToolParams, notes: 'Sub-agent tool' },
  '@n8n/n8n-nodes-langchain.toolThink': { ewaType: 'no-op', notes: 'Think tool - internal reasoning step' },
  '@n8n/n8n-nodes-langchain.toolCalculator': { ewaType: 'function', notes: 'Calculator - use function node' },
  '@n8n/n8n-nodes-langchain.toolWikipedia': { ewaType: 'http-request', notes: 'Wikipedia tool - use HTTP request' },
  '@n8n/n8n-nodes-langchain.toolSerpApi': { ewaType: 'brave-search', notes: 'SERP API - use Brave Search' },
  '@n8n/n8n-nodes-langchain.toolHttpRequest': { ewaType: 'http-request', notes: 'HTTP tool for AI agents' },

  // Vector Stores & Embeddings
  '@n8n/n8n-nodes-langchain.vectorStoreQdrant': { ewaType: 'langchain-vector-store', parameterTransform: transformVectorStoreQdrantParams },
  '@n8n/n8n-nodes-langchain.vectorStoreInMemory': { ewaType: 'langchain-vector-store', notes: 'In-memory vector store' },
  '@n8n/n8n-nodes-langchain.embeddingsAzureOpenAi': { ewaType: 'langchain-embeddings', parameterTransform: transformEmbeddingsAzureParams },
  '@n8n/n8n-nodes-langchain.embeddingsOpenAi': { ewaType: 'langchain-embeddings', parameterTransform: transformEmbeddingsOpenAiParams },
  '@n8n/n8n-nodes-langchain.embeddingsGoogleGemini': { ewaType: 'langchain-embeddings', notes: 'Google Gemini embeddings' },
  '@n8n/n8n-nodes-langchain.embeddingsOllama': { ewaType: 'langchain-embeddings', notes: 'Ollama embeddings' },
  '@n8n/n8n-nodes-langchain.embeddingsHuggingFaceInference': { ewaType: 'langchain-embeddings', notes: 'HuggingFace embeddings' },
  '@n8n/n8n-nodes-langchain.documentDefaultDataLoader': { ewaType: 'langchain-document-loader', parameterTransform: transformDocumentLoaderParams },
  '@n8n/n8n-nodes-langchain.textSplitterTokenSplitter': { ewaType: 'langchain-text-splitter' },
  '@n8n/n8n-nodes-langchain.textSplitterCharacterTextSplitter': { ewaType: 'langchain-text-splitter' },
  '@n8n/n8n-nodes-langchain.textSplitterRecursiveCharacterTextSplitter': { ewaType: 'langchain-text-splitter' },
  '@n8n/n8n-nodes-langchain.retrieverVectorStore': { ewaType: 'langchain-vector-store', notes: 'Vector store retriever' },

  // =========================================================================
  // CLOUD STORAGE / FILES
  // =========================================================================
  'n8n-nodes-base.ftp': { ewaType: 'ftp', parameterTransform: transformFtpParams },
  'n8n-nodes-base.azureStorage': { ewaType: 'azure-blob-storage', parameterTransform: transformAzureStorageParams },
  'n8n-nodes-base.awsS3': { ewaType: 'no-op', notes: 'AWS S3 - coming soon in EWA' },
  'n8n-nodes-base.dropbox': { ewaType: 'no-op', notes: 'Dropbox - coming soon in EWA' },
  'n8n-nodes-base.googleDrive': { ewaType: 'no-op', notes: 'Google Drive - coming soon in EWA' },

  // =========================================================================
  // MICROSOFT INTEGRATIONS
  // =========================================================================
  'n8n-nodes-base.microsoftOutlook': { ewaType: 'microsoft-outlook', parameterTransform: transformMicrosoftOutlookParams },
  'n8n-nodes-base.microsoftOutlookTool': { ewaType: 'microsoft-outlook', notes: 'Outlook tool for AI agents' },
  'n8n-nodes-base.microsoftTeams': { ewaType: 'microsoft-teams', parameterTransform: transformMicrosoftTeamsParams },
  'n8n-nodes-base.microsoftTeamsTool': { ewaType: 'microsoft-teams', notes: 'Teams tool for AI agents' },
  'n8n-nodes-base.microsoftSharePoint': { ewaType: 'microsoft-sharepoint', parameterTransform: transformMicrosoftSharePointParams },
  'n8n-nodes-base.microsoftSharePointTool': { ewaType: 'microsoft-sharepoint', notes: 'SharePoint tool for AI agents' },
  'n8n-nodes-base.microsoftOneDrive': { ewaType: 'microsoft-onedrive', parameterTransform: transformMicrosoftOneDriveParams },
  'n8n-nodes-base.microsoftExcel': { ewaType: 'microsoft-onedrive', notes: 'Excel via OneDrive' },
  'n8n-nodes-base.microsoftEntra': { ewaType: 'http-request', notes: 'Microsoft Entra - use HTTP with OAuth' },
  'n8n-nodes-base.microsoftDevOps': { ewaType: 'http-request', notes: 'Azure DevOps - use HTTP request' },
  'n8n-nodes-base.azureDevOps': { ewaType: 'http-request', notes: 'Azure DevOps - use HTTP request' },

  // =========================================================================
  // COMMUNICATION / EMAIL
  // =========================================================================
  'n8n-nodes-base.emailSend': { ewaType: 'smtp-email', parameterTransform: transformEmailSendParams },
  'n8n-nodes-base.sendEmail': { ewaType: 'smtp-email', parameterTransform: transformEmailSendParams },
  'n8n-nodes-base.emailSendTool': { ewaType: 'smtp-email', notes: 'Email tool for AI agents' },
  'n8n-nodes-base.gmail': { ewaType: 'smtp-email', notes: 'Gmail - use SMTP (Gmail coming soon)' },
  'n8n-nodes-base.gmailTool': { ewaType: 'smtp-email', notes: 'Gmail tool - use SMTP' },
  'n8n-nodes-base.mailgun': { ewaType: 'smtp-email', notes: 'Mailgun - use SMTP' },
  'n8n-nodes-base.sendGrid': { ewaType: 'smtp-email', notes: 'SendGrid - coming soon in EWA' },
  'n8n-nodes-base.pushover': { ewaType: 'http-request', notes: 'Pushover - use HTTP request' },

  // Messaging Platforms (coming soon)
  'n8n-nodes-base.slack': { ewaType: 'no-op', parameterTransform: transformSlackParams, notes: 'Slack - coming soon in EWA' },
  'n8n-nodes-base.slackTool': { ewaType: 'no-op', notes: 'Slack tool - coming soon' },
  'n8n-nodes-base.discord': { ewaType: 'no-op', notes: 'Discord - coming soon in EWA' },
  'n8n-nodes-base.telegram': { ewaType: 'http-request', notes: 'Telegram - use HTTP request' },
  'n8n-nodes-base.telegramTool': { ewaType: 'http-request', notes: 'Telegram tool - use HTTP' },
  'n8n-nodes-base.whatsApp': { ewaType: 'http-request', notes: 'WhatsApp - use HTTP request' },
  'n8n-nodes-base.twilio': { ewaType: 'http-request', notes: 'Twilio - coming soon in EWA' },

  // =========================================================================
  // PROJECT MANAGEMENT (coming soon in EWA)
  // =========================================================================
  'n8n-nodes-base.jira': { ewaType: 'no-op', parameterTransform: transformJiraParams, notes: 'Jira - coming soon in EWA' },
  'n8n-nodes-base.asana': { ewaType: 'no-op', parameterTransform: transformAsanaParams, notes: 'Asana - coming soon in EWA' },
  'n8n-nodes-base.asanaTool': { ewaType: 'no-op', notes: 'Asana tool - coming soon' },
  'n8n-nodes-base.notion': { ewaType: 'no-op', notes: 'Notion - coming soon in EWA' },
  'n8n-nodes-base.trello': { ewaType: 'no-op', notes: 'Trello - coming soon in EWA' },
  'n8n-nodes-base.linear': { ewaType: 'no-op', notes: 'Linear - coming soon in EWA' },

  // =========================================================================
  // CRM / SALES (coming soon in EWA)
  // =========================================================================
  'n8n-nodes-base.salesforce': { ewaType: 'no-op', parameterTransform: transformSalesforceParams, notes: 'Salesforce - coming soon in EWA' },
  'n8n-nodes-base.hubspot': { ewaType: 'no-op', notes: 'HubSpot - coming soon in EWA' },

  // =========================================================================
  // DEVELOPER TOOLS
  // =========================================================================
  'n8n-nodes-base.github': { ewaType: 'no-op', parameterTransform: transformGitHubParams, notes: 'GitHub - coming soon in EWA' },
  'n8n-nodes-base.githubTool': { ewaType: 'no-op', notes: 'GitHub tool - coming soon' },
  'n8n-nodes-base.gitlab': { ewaType: 'no-op', notes: 'GitLab - coming soon in EWA' },
  'n8n-nodes-base.git': { ewaType: 'no-op', parameterTransform: transformGitParams, notes: 'Git operations - not supported in EWA' },
  'n8n-nodes-base.gitTool': { ewaType: 'no-op', notes: 'Git tool - not supported' },

  // =========================================================================
  // GOOGLE SUITE (coming soon in EWA)
  // =========================================================================
  'n8n-nodes-base.googleSheets': { ewaType: 'no-op', parameterTransform: transformGoogleSheetsParams, notes: 'Google Sheets - coming soon in EWA' },
  'n8n-nodes-base.googleSheetsTool': { ewaType: 'no-op', notes: 'Google Sheets tool - coming soon' },
  'n8n-nodes-base.googleDocs': { ewaType: 'no-op', notes: 'Google Docs - coming soon in EWA' },
  'n8n-nodes-base.googleCalendar': { ewaType: 'no-op', notes: 'Google Calendar - coming soon in EWA' },
  'n8n-nodes-base.googleCalendarTool': { ewaType: 'no-op', notes: 'Google Calendar tool - coming soon' },
  'n8n-nodes-base.googleTasksTool': { ewaType: 'no-op', notes: 'Google Tasks tool - coming soon' },
  'n8n-nodes-base.googleAnalytics': { ewaType: 'http-request', notes: 'Google Analytics - use HTTP' },

  // =========================================================================
  // SEARCH / RSS
  // =========================================================================
  'n8n-nodes-base.braveSearch': { ewaType: 'brave-search' },
  'n8n-nodes-base.rssFeedRead': { ewaType: 'http-request', parameterTransform: transformRssFeedParams, notes: 'RSS - use HTTP request' },
  'n8n-nodes-base.rssFeedReadTool': { ewaType: 'http-request', notes: 'RSS tool - use HTTP' },

  // =========================================================================
  // PAYMENTS (coming soon in EWA)
  // =========================================================================
  'n8n-nodes-base.stripe': { ewaType: 'no-op', notes: 'Stripe - coming soon in EWA' },
  'n8n-nodes-base.paypal': { ewaType: 'no-op', notes: 'PayPal - coming soon in EWA' },

  // =========================================================================
  // UTILITIES / MISC
  // =========================================================================
  'n8n-nodes-base.stickyNote': { ewaType: 'no-op', notes: 'Sticky notes become no-op' },
  'n8n-nodes-base.executeCommand': { ewaType: 'code', parameterTransform: transformExecuteCommandParams, notes: 'Shell via code node' },
  'n8n-nodes-base.moveBinaryData': { ewaType: 'binary-to-text' },
  'n8n-nodes-base.executionData': { ewaType: 'no-op', notes: 'Execution context data - use workflow variables' },
  'n8n-nodes-base.n8n': { ewaType: 'no-op', notes: 'n8n API node - not applicable' },
  'n8n-nodes-base.evaluation': { ewaType: 'no-op', notes: 'AI evaluation - not yet supported' },
  'n8n-nodes-base.logger': { ewaType: 'no-op', notes: 'Logger - use code node' },
  'n8n-nodes-base.jwt': { ewaType: 'crypto', notes: 'JWT - use crypto node' },
  'n8n-nodes-base.openWeatherMap': { ewaType: 'http-request', notes: 'Weather API - use HTTP' },
  'n8n-nodes-base.nasa': { ewaType: 'http-request', notes: 'NASA API - use HTTP' },
  'n8n-nodes-base.hackerNews': { ewaType: 'http-request', notes: 'Hacker News - use HTTP' },
  'n8n-nodes-base.uproc': { ewaType: 'http-request', notes: 'uProc - use HTTP' },
  'n8n-nodes-base.graphqlTool': { ewaType: 'graphql', notes: 'GraphQL tool for AI agents' },

  // =========================================================================
  // THIRD-PARTY / COMMUNITY NODES
  // =========================================================================
  'n8n-nodes-docxtemplater.docxTemplater': { ewaType: 'no-op', notes: 'DocX templating - use HTTP request to document service' },
};

/**
 * Get EWA node mapping for an n8n node type
 */
export function getEwaNodeType(n8nType: string): NodeMapping | null {
  return N8N_TO_EWA_NODE_MAP[n8nType] || null;
}

/**
 * Get list of all supported n8n node types
 */
export function getSupportedN8nNodeTypes(): string[] {
  return Object.keys(N8N_TO_EWA_NODE_MAP);
}

// =============================================================================
// PARAMETER TRANSFORMERS
// =============================================================================

function transformScheduleParams(n8nParams: Record<string, unknown>): TransformParamResult {
  const unsupportedParams: Array<{ name: string; value: unknown; reason: string }> = [];
  const parameters: Record<string, unknown> = {
    triggerTimes: { item: [] as Record<string, unknown>[] },
    timezone: 'UTC',
  };

  const rule = n8nParams.rule as Record<string, unknown> | undefined;
  const intervals = rule?.interval as Array<Record<string, unknown>> | undefined;
  const items = (parameters.triggerTimes as Record<string, unknown>).item as Record<string, unknown>[];

  if (intervals && intervals.length > 0) {
    for (const interval of intervals) {
      // n8n Schedule Trigger: field can be undefined for simple daily schedules
      // When no field is specified, it defaults to daily trigger
      const field = interval.field as string | undefined;
      const hour = (interval.triggerAtHour as number) ?? 0;
      const minute = (interval.triggerAtMinute as number) ?? 0;

      // If no field specified but has triggerAtHour, it's a daily schedule
      if (!field && (interval.triggerAtHour !== undefined || interval.triggerAtMinute !== undefined)) {
        items.push({
          mode: 'everyDay',
          hour,
          minute,
        });
        continue;
      }

      switch (field) {
        case 'minutes':
          items.push({
            mode: 'everyXMinutes',
            minuteInterval: interval.minutesInterval || 15,
          });
          break;
        case 'hours':
          items.push({
            mode: 'everyXHours',
            hourInterval: interval.hoursInterval || 2,
            startHour: 0,
          });
          break;
        case 'days':
          items.push({
            mode: 'everyDay',
            hour,
            minute,
          });
          break;
        case 'weeks': {
          const triggerAtDay = interval.triggerAtDay;
          let weekDays: number[];

          if (Array.isArray(triggerAtDay)) {
            weekDays = triggerAtDay as number[];
          } else if (typeof triggerAtDay === 'number') {
            weekDays = [triggerAtDay];
          } else {
            weekDays = [0];
          }

          for (const day of weekDays) {
            items.push({
              mode: 'everyWeek',
              hour,
              minute,
              dayOfWeek: day,
            });
          }
          break;
        }
        case 'months':
          items.push({
            mode: 'everyMonth',
            hour,
            minute,
            dayOfMonth: (interval.triggerAtDay as number) || 1,
          });
          break;
        case 'cronExpression':
          items.push({
            mode: 'custom',
            cronExpression: interval.expression as string,
          });
          break;
        case undefined:
          // No field and no time = default to every hour
          items.push({
            mode: 'everyHour',
            minute: 0,
          });
          break;
        default:
          unsupportedParams.push({
            name: 'interval.field',
            value: field,
            reason: `Schedule field "${field}" not yet supported`,
          });
      }
    }
  }

  return { parameters, unsupportedParams };
}

function transformCronParams(n8nParams: Record<string, unknown>): TransformParamResult {
  const unsupportedParams: Array<{ name: string; value: unknown; reason: string }> = [];
  const parameters: Record<string, unknown> = {
    triggerTimes: { item: [] as Record<string, unknown>[] },
  };

  const cronExpression = n8nParams.cronExpression as string;
  if (cronExpression) {
    (parameters.triggerTimes as Record<string, unknown>).item = [{
      mode: 'custom',
      cronExpression,
    }];
  }

  return { parameters, unsupportedParams };
}

function transformWebhookParams(n8nParams: Record<string, unknown>): TransformParamResult {
  const unsupportedParams: Array<{ name: string; value: unknown; reason: string }> = [];

  // Map n8n authentication types to EWA
  let authentication = 'none';
  if (n8nParams.authentication) {
    const authMap: Record<string, string> = {
      'basicAuth': 'basicAuth',
      'headerAuth': 'headerAuth',
      'jwt': 'jwtAuth',
    };
    authentication = authMap[n8nParams.authentication as string] || 'none';
  }

  // Map response mode
  let responseMode = 'immediate';
  if (n8nParams.responseMode) {
    const responseModeMap: Record<string, string> = {
      'onReceived': 'immediate',
      'lastNode': 'last-node',
      'responseNode': 'respond-node',
    };
    responseMode = responseModeMap[n8nParams.responseMode as string] || 'immediate';
  }

  return {
    parameters: {
      path: n8nParams.path || '',
      method: n8nParams.httpMethod || 'POST',  // n8n uses httpMethod, EWA uses method
      authentication,
      responseMode,
    },
    unsupportedParams,
  };
}

function transformFormTriggerParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      path: n8nParams.path || '/form',
      httpMethod: 'POST',
    },
    unsupportedParams: [],
  };
}

function transformMicrosoftTeamsTriggerParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      _n8nOriginalType: 'microsoftTeamsTrigger',
      _n8nConfig: n8nParams,
    },
    unsupportedParams: [{
      name: 'teamsConfig',
      value: n8nParams,
      reason: 'Microsoft Teams trigger requires manual configuration in EWA polling trigger',
    }],
  };
}

function transformIfParams(n8nParams: Record<string, unknown>): TransformParamResult {
  const unsupportedParams: Array<{ name: string; value: unknown; reason: string }> = [];

  // n8n If node v2 structure:
  // { conditions: { combinator: 'and', conditions: [{ leftValue, rightValue, operator: { type, operation } }] } }
  //
  // EWA If node structure:
  // { conditions: { values: [{ field1, operator, value }] }, combineOperation: 'AND' | 'OR' }

  const n8nConditions = n8nParams.conditions as Record<string, unknown> | undefined;
  const ewaConditions: Array<Record<string, unknown>> = [];

  if (n8nConditions?.conditions && Array.isArray(n8nConditions.conditions)) {
    for (const cond of n8nConditions.conditions as Array<Record<string, unknown>>) {
      const operator = cond.operator as Record<string, unknown> | undefined;
      const n8nOperation = operator?.operation as string || 'equals';

      // Map n8n operators to EWA operators
      const operatorMap: Record<string, string> = {
        'equals': '===',
        'notEquals': '!==',
        'gt': '>',
        'gte': '>=',
        'lt': '<',
        'lte': '<=',
        'contains': 'contains',
        'notContains': '!==', // approximation
        'startsWith': 'startsWith',
        'endsWith': 'endsWith',
        'empty': 'isEmpty',
        'notEmpty': 'isNotEmpty',
        'regex': '===', // approximation - regex not directly supported
        'isTrue': '===',
        'isFalse': '===',
      };

      ewaConditions.push({
        field1: cond.leftValue || '',
        operator: operatorMap[n8nOperation] || '===',
        value: cond.rightValue || '',
      });
    }
  }

  // Map combinator
  const combinator = n8nConditions?.combinator as string || 'and';
  const combineOperation = combinator.toLowerCase() === 'or' ? 'OR' : 'AND';

  return {
    parameters: {
      conditions: {
        values: ewaConditions,
      },
      combineOperation,
    },
    unsupportedParams,
  };
}

function transformSwitchParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      rules: n8nParams.rules,
      fallbackOutput: n8nParams.fallbackOutput,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformMergeParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      mode: n8nParams.mode || 'append',
      joinMode: n8nParams.joinMode,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformSplitInBatchesParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      batchSize: n8nParams.batchSize || 10,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformExecuteWorkflowParams(n8nParams: Record<string, unknown>): TransformParamResult {
  const unsupportedParams: Array<{ name: string; value: unknown; reason: string }> = [];

  if (n8nParams.workflowId) {
    unsupportedParams.push({
      name: 'workflowId',
      value: n8nParams.workflowId,
      reason: 'Workflow ID reference needs to be reconfigured in EWA',
    });
  }

  return {
    parameters: {
      _n8nWorkflowId: n8nParams.workflowId,
      mode: n8nParams.mode,
    },
    unsupportedParams,
  };
}

function transformWaitParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      amount: n8nParams.amount || 1,
      unit: n8nParams.unit || 'seconds',
    },
    unsupportedParams: [],
  };
}

function transformFilterParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      conditions: n8nParams.conditions,
      combineOperation: n8nParams.combineOperation || 'all',
    },
    unsupportedParams: [],
  };
}

function transformLimitParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      maxItems: n8nParams.maxItems || 10,
      keep: n8nParams.keep || 'firstItems',
    },
    unsupportedParams: [],
  };
}

function transformStopAndErrorParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      _originalType: 'stopAndError',
      _errorMessage: n8nParams.errorMessage,
      _errorType: n8nParams.errorType,
    },
    unsupportedParams: [{
      name: 'stopAndError',
      value: n8nParams,
      reason: 'Stop and Error node - workflow will continue but error info preserved',
    }],
  };
}

function transformHttpRequestParams(n8nParams: Record<string, unknown>): TransformParamResult {
  const unsupportedParams: Array<{ name: string; value: unknown; reason: string }> = [];
  const options = n8nParams.options as Record<string, unknown> | undefined;

  if (options?.proxy) {
    unsupportedParams.push({
      name: 'options.proxy',
      value: options.proxy,
      reason: 'Proxy configuration not yet supported',
    });
  }

  if (options?.batching) {
    unsupportedParams.push({
      name: 'options.batching',
      value: options.batching,
      reason: 'Request batching not yet supported',
    });
  }

  // Determine body content type - n8n uses different param names across versions
  // 'specifyBody' in newer versions, 'contentType' or 'bodyContentType' in older
  let bodyContentType: string = 'json';
  if (n8nParams.specifyBody) {
    bodyContentType = n8nParams.specifyBody as string;
  } else if (n8nParams.contentType) {
    bodyContentType = n8nParams.contentType as string;
  } else if (n8nParams.bodyContentType) {
    bodyContentType = n8nParams.bodyContentType as string;
  }

  // Map n8n body content types to EWA types
  const contentTypeMap: Record<string, string> = {
    'json': 'json',
    'raw': 'raw',
    'form-urlencoded': 'form-urlencoded',
    'multipart-form-data': 'multipart-form-data',
    'binaryData': 'binaryData',
  };

  // Determine authentication type
  let authentication = n8nParams.authentication as string || 'none';
  if (n8nParams.genericAuthType) {
    // n8n uses genericAuthType for the actual auth method when authentication is 'genericCredentialType'
    const authTypeMap: Record<string, string> = {
      'httpBasicAuth': 'basicAuth',
      'httpHeaderAuth': 'headerAuth',
      'httpQueryAuth': 'queryAuth',
      'oAuth2Api': 'oauth2',
    };
    authentication = authTypeMap[n8nParams.genericAuthType as string] || 'none';
  }

  // Handle JSON body - can be in 'body' or 'jsonBody' field
  let jsonBody = n8nParams.body;
  if (typeof jsonBody === 'object' && jsonBody !== null) {
    jsonBody = JSON.stringify(jsonBody);
  }

  return {
    parameters: {
      method: n8nParams.method || 'GET',
      url: n8nParams.url || '',
      authentication,
      sendHeaders: n8nParams.sendHeaders || false,
      headerParameters: n8nParams.headerParameters,
      sendQuery: n8nParams.sendQuery || false,
      queryParameters: n8nParams.queryParameters,
      sendBody: n8nParams.sendBody || false,
      bodyContentType: contentTypeMap[bodyContentType] || 'json',
      jsonBody: jsonBody || '{}',
      bodyParameters: n8nParams.bodyParameters,
      rawBody: n8nParams.rawBody || '',
      options: {
        timeout: options?.timeout || 10000,
        followRedirect: options?.followRedirects !== false,
        ignoreResponseCode: options?.ignoreResponseCode || false,
      },
    },
    unsupportedParams,
  };
}

function transformRespondToWebhookParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      respondWith: n8nParams.respondWith || 'firstIncomingItem',
      responseBody: n8nParams.responseBody,
      responseCode: n8nParams.responseCode || 200,
      responseHeaders: n8nParams.responseHeaders,
    },
    unsupportedParams: [],
  };
}

function transformGraphqlParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      endpoint: n8nParams.endpoint,
      query: n8nParams.query,
      variables: n8nParams.variables,
      requestFormat: n8nParams.requestFormat,
      responseFormat: n8nParams.responseFormat,
    },
    unsupportedParams: [],
  };
}

function transformSqlParams(n8nParams: Record<string, unknown>): TransformParamResult {
  const unsupportedParams: Array<{ name: string; value: unknown; reason: string }> = [];
  const options = n8nParams.options as Record<string, unknown> | undefined;

  if (options?.queryBatching) {
    unsupportedParams.push({
      name: 'options.queryBatching',
      value: options.queryBatching,
      reason: 'Query batching not yet supported',
    });
  }

  return {
    parameters: {
      // Operation - default to executeQuery
      operation: n8nParams.operation || 'executeQuery',
      // Query - the SQL to execute
      query: n8nParams.query || '',
      // Table name (for non-query operations)
      table: n8nParams.table || '',
      // Query timeout
      queryTimeout: options?.queryTimeout || 120,
    },
    unsupportedParams,
  };
}

function transformPostgresParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      query: n8nParams.query,
      schema: n8nParams.schema,
      table: n8nParams.table,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformMysqlParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      query: n8nParams.query,
      table: n8nParams.table,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformMongoDbParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      collection: n8nParams.collection,
      query: n8nParams.query,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformRedisParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      key: n8nParams.key,
      value: n8nParams.value,
      keyType: n8nParams.keyType,
    },
    unsupportedParams: [],
  };
}

function transformSnowflakeParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation || 'executeQuery',
      query: n8nParams.query,
      _dialect: 'snowflake',
    },
    unsupportedParams: [{
      name: 'snowflakeDialect',
      value: 'snowflake',
      reason: 'Snowflake-specific SQL syntax may need adjustment',
    }],
  };
}

function transformCodeParams(n8nParams: Record<string, unknown>): TransformParamResult {
  // n8n Code node uses 'jsCode', EWA uses 'code'
  return {
    parameters: {
      code: n8nParams.jsCode || '',
      language: n8nParams.language || 'javascript',
      executionTier: 'sandboxed',
    },
    unsupportedParams: [],
  };
}

function transformFunctionParams(n8nParams: Record<string, unknown>): TransformParamResult {
  // n8n Function node (legacy) uses 'functionCode'
  return {
    parameters: {
      code: n8nParams.functionCode || n8nParams.jsCode || '',
      language: 'javascript',
      executionTier: 'sandboxed',
    },
    unsupportedParams: [],
  };
}

function transformFunctionItemParams(n8nParams: Record<string, unknown>): TransformParamResult {
  // n8n Function Item node runs code once for each item (legacy)
  return {
    parameters: {
      code: n8nParams.functionCode || n8nParams.jsCode || '',
      language: 'javascript',
      executionTier: 'sandboxed',
    },
    unsupportedParams: [],
  };
}

function transformSetParams(n8nParams: Record<string, unknown>): TransformParamResult {
  // n8n Set node structure:
  // { assignments: { assignments: [{ id, name, value, type }] }, options: {} }
  //
  // EWA edit-fields structure:
  // { mode: 'manual', fields: { values: [{ operation, name, fieldType, value }] }, includeOtherFields: true }

  const n8nAssignments = n8nParams.assignments as Record<string, unknown> | undefined;
  const assignmentsList = n8nAssignments?.assignments as Array<Record<string, unknown>> | undefined;

  // Transform n8n assignments to EWA field operations
  const ewaFields: Array<Record<string, unknown>> = [];

  if (assignmentsList && Array.isArray(assignmentsList)) {
    for (const assignment of assignmentsList) {
      const fieldType = assignment.type as string || 'string';
      const fieldName = assignment.name as string || '';
      const fieldValue = assignment.value;

      // Determine the appropriate value key based on type
      const ewaField: Record<string, unknown> = {
        operation: 'set',
        name: fieldName,
        fieldType: fieldType === 'number' ? 'number' :
                   fieldType === 'boolean' ? 'boolean' :
                   fieldType === 'object' || fieldType === 'array' ? 'json' : 'string',
      };

      // Set the value in the appropriate field based on type
      if (fieldType === 'number') {
        ewaField.numberValue = fieldValue;
      } else if (fieldType === 'boolean') {
        ewaField.booleanValue = fieldValue;
      } else if (fieldType === 'object' || fieldType === 'array') {
        ewaField.jsonValue = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
      } else {
        ewaField.value = fieldValue;
      }

      ewaFields.push(ewaField);
    }
  }

  // Check if we should include other fields (n8n options.includeOtherFields)
  const options = n8nParams.options as Record<string, unknown> | undefined;
  const includeOther = options?.includeOtherFields !== false;

  return {
    parameters: {
      mode: 'manual',
      fields: {
        values: ewaFields,
      },
      includeOtherFields: includeOther,
    },
    unsupportedParams: [],
  };
}

function transformAggregateParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      aggregate: n8nParams.aggregate,
      fieldsToAggregate: n8nParams.fieldsToAggregate,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformSortParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      sortFieldsUi: n8nParams.sortFieldsUi,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformRemoveDuplicatesParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      compare: n8nParams.compare,
      fieldsToCompare: n8nParams.fieldsToCompare,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformDateTimeParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      action: n8nParams.action,
      value: n8nParams.value,
      fromFormat: n8nParams.fromFormat,
      toFormat: n8nParams.toFormat,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformCryptoParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      action: n8nParams.action,
      type: n8nParams.type,
      value: n8nParams.value,
      encoding: n8nParams.encoding,
    },
    unsupportedParams: [],
  };
}

function transformXmlParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      mode: n8nParams.mode,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformHtmlParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      sourceData: n8nParams.sourceData,
      extractionValues: n8nParams.extractionValues,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformHtmlExtractParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      sourceData: n8nParams.sourceData,
      extractionValues: n8nParams.extractionValues,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformCompareDataParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      input1: n8nParams.input1,
      input2: n8nParams.input2,
      mergeByFields: n8nParams.mergeByFields,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformItemListsParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      fieldToSplitOut: n8nParams.fieldToSplitOut,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformSplitOutParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      fieldToSplitOut: n8nParams.fieldToSplitOut,
      include: n8nParams.include,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformSpreadsheetParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      fileFormat: n8nParams.fileFormat,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformExtractFromFileParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      sourceProperty: n8nParams.sourceProperty,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformConvertToFileParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      fileName: n8nParams.fileName,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformAgentParams(n8nParams: Record<string, unknown>): TransformParamResult {
  const options = n8nParams.options as Record<string, unknown> | undefined;

  return {
    parameters: {
      // Map n8n 'text' to EWA 'userPrompt'
      userPrompt: n8nParams.text || n8nParams.prompt || '',
      // Map n8n 'options.systemMessage' to EWA 'systemPrompt'
      systemPrompt: options?.systemMessage || 'You are a helpful AI assistant.',
      // Max iterations
      maxIterations: options?.maxIterations || 10,
      // Return intermediate steps
      returnIntermediateSteps: options?.returnIntermediateSteps || false,
      // Preserve original options for reference
      _n8nOptions: options,
    },
    unsupportedParams: [],
  };
}

function transformLmChatOpenAiParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      model: n8nParams.model,
      temperature: (n8nParams.options as Record<string, unknown>)?.temperature,
      maxTokens: (n8nParams.options as Record<string, unknown>)?.maxTokens,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformLmChatAnthropicParams(n8nParams: Record<string, unknown>): TransformParamResult {
  const options = n8nParams.options as Record<string, unknown> | undefined;

  // n8n uses resource locator pattern for model - can be string or {__rl: true, value: "model-id"}
  let model = n8nParams.model;
  if (model && typeof model === 'object' && '__rl' in model) {
    model = (model as Record<string, unknown>).value;
  }

  return {
    parameters: {
      // Model - extract from resource locator if needed
      model: model || 'claude-haiku-4-5-20251001',
      // Temperature (convert to string as EWA expects)
      temperature: options?.temperature !== undefined ? String(options.temperature) : undefined,
      // Max tokens
      maxTokens: options?.maxTokensToSample !== undefined ? String(options.maxTokensToSample) : undefined,
      // Top P
      topP: options?.topP !== undefined ? String(options.topP) : undefined,
    },
    unsupportedParams: [],
  };
}

function transformLmChatAzureOpenAiParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      model: n8nParams.model,
      deploymentName: n8nParams.deploymentName,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformLmChatGoogleGeminiParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      model: n8nParams.model,
      _originalProvider: 'google-gemini',
      options: n8nParams.options,
    },
    unsupportedParams: [{
      name: 'provider',
      value: 'google-gemini',
      reason: 'Google Gemini mapped to OpenAI-compatible endpoint',
    }],
  };
}

function transformMemoryBufferParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      sessionIdType: n8nParams.sessionIdType,
      sessionKey: n8nParams.sessionKey,
      contextWindowLength: n8nParams.contextWindowLength,
    },
    unsupportedParams: [],
  };
}

function transformChainLlmParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      prompt: n8nParams.prompt || n8nParams.text,
      _originalType: 'chainLlm',
    },
    unsupportedParams: [],
  };
}

function transformOutputParserParams(n8nParams: Record<string, unknown>): TransformParamResult {
  // n8n uses 'jsonSchemaExample' for example-based schema, 'jsonSchema' for manual schema
  const hasExample = !!n8nParams.jsonSchemaExample;

  return {
    parameters: {
      // Determine schema source based on what's provided
      schemaSource: hasExample ? 'example' : 'manual',
      // Map n8n 'jsonSchemaExample' to EWA 'jsonExample'
      jsonExample: n8nParams.jsonSchemaExample || '{}',
      // Manual JSON schema
      jsonSchema: n8nParams.jsonSchema || '{}',
      // Auto-fix setting
      enableAutoFix: n8nParams.autoFix !== false,
      // Strict validation
      strictValidation: true,
      // Retry settings
      enableRetry: true,
      maxRetries: 3,
    },
    unsupportedParams: [],
  };
}

function transformOpenAiParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation || n8nParams.resource,
      model: n8nParams.model,
      prompt: n8nParams.prompt,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformToolCodeParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      jsCode: n8nParams.jsCode,
      toolDescription: n8nParams.description || n8nParams.toolDescription,
    },
    unsupportedParams: [],
  };
}

function transformInformationExtractorParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      text: n8nParams.text,
      schemaType: n8nParams.schemaType,
      jsonSchema: n8nParams.jsonSchema,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformAgentToolParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      toolDescription: n8nParams.toolDescription || n8nParams.description,
      text: n8nParams.text,
      hasOutputParser: n8nParams.hasOutputParser,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformVectorStoreQdrantParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      mode: n8nParams.mode,
      qdrantCollection: n8nParams.qdrantCollection,
      options: n8nParams.options,
      _provider: 'qdrant',
    },
    unsupportedParams: [],
  };
}

function transformEmbeddingsAzureParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      model: n8nParams.model,
      options: n8nParams.options,
      _provider: 'azure-openai',
    },
    unsupportedParams: [],
  };
}

function transformEmbeddingsOpenAiParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      model: n8nParams.model,
      options: n8nParams.options,
      _provider: 'openai',
    },
    unsupportedParams: [],
  };
}

function transformDocumentLoaderParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      dataType: n8nParams.dataType,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformFtpParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      path: n8nParams.path,
      binaryPropertyName: n8nParams.binaryPropertyName,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformAzureStorageParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      container: n8nParams.container,
      blobName: n8nParams.blobName,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformMicrosoftOutlookParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      resource: n8nParams.resource,
      subject: n8nParams.subject,
      bodyContent: n8nParams.bodyContent,
      toRecipients: n8nParams.toRecipients,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformMicrosoftTeamsParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      resource: n8nParams.resource,
      teamId: n8nParams.teamId,
      channelId: n8nParams.channelId,
      chatId: n8nParams.chatId,
      message: n8nParams.message,
      messageType: n8nParams.messageType,
      contentType: n8nParams.contentType,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformMicrosoftSharePointParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      siteId: n8nParams.siteId,
      listId: n8nParams.listId,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformMicrosoftOneDriveParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      operation: n8nParams.operation,
      fileId: n8nParams.fileId,
      filePath: n8nParams.filePath,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformEmailSendParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      fromEmail: n8nParams.fromEmail,
      toEmail: n8nParams.toEmail,
      subject: n8nParams.subject,
      text: n8nParams.text,
      html: n8nParams.html,
      attachments: n8nParams.attachments,
      options: n8nParams.options,
    },
    unsupportedParams: [],
  };
}

function transformSlackParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      _originalType: 'slack',
      operation: n8nParams.operation,
      channel: n8nParams.channel,
      text: n8nParams.text,
      options: n8nParams.options,
    },
    unsupportedParams: [{
      name: 'slackIntegration',
      value: n8nParams,
      reason: 'Slack integration coming soon in EWA',
    }],
  };
}

function transformJiraParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      _originalType: 'jira',
      operation: n8nParams.operation,
      resource: n8nParams.resource,
      issueKey: n8nParams.issueKey,
      options: n8nParams.options,
    },
    unsupportedParams: [{
      name: 'jiraIntegration',
      value: n8nParams,
      reason: 'Jira integration coming soon in EWA',
    }],
  };
}

function transformAsanaParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      _originalType: 'asana',
      operation: n8nParams.operation,
      resource: n8nParams.resource,
      options: n8nParams.options,
    },
    unsupportedParams: [{
      name: 'asanaIntegration',
      value: n8nParams,
      reason: 'Asana integration coming soon in EWA',
    }],
  };
}

function transformSalesforceParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      _originalType: 'salesforce',
      operation: n8nParams.operation,
      resource: n8nParams.resource,
      options: n8nParams.options,
    },
    unsupportedParams: [{
      name: 'salesforceIntegration',
      value: n8nParams,
      reason: 'Salesforce integration coming soon in EWA',
    }],
  };
}

function transformGitHubParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      _originalType: 'github',
      operation: n8nParams.operation,
      resource: n8nParams.resource,
      owner: n8nParams.owner,
      repository: n8nParams.repository,
      options: n8nParams.options,
    },
    unsupportedParams: [{
      name: 'githubIntegration',
      value: n8nParams,
      reason: 'GitHub integration coming soon in EWA',
    }],
  };
}

function transformGitParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      _originalType: 'git',
      operation: n8nParams.operation,
      repositoryPath: n8nParams.repositoryPath,
      sourceRepository: n8nParams.sourceRepository,
    },
    unsupportedParams: [{
      name: 'gitOperations',
      value: n8nParams,
      reason: 'Git operations not supported in EWA cloud environment',
    }],
  };
}

function transformGoogleSheetsParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      _originalType: 'googleSheets',
      operation: n8nParams.operation,
      documentId: n8nParams.documentId,
      sheetName: n8nParams.sheetName,
      options: n8nParams.options,
    },
    unsupportedParams: [{
      name: 'googleSheetsIntegration',
      value: n8nParams,
      reason: 'Google Sheets integration coming soon in EWA',
    }],
  };
}

function transformRssFeedParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      url: n8nParams.url,
      options: n8nParams.options,
      _note: 'Use HTTP request to fetch RSS XML, then parse with XML node',
    },
    unsupportedParams: [],
  };
}

function transformExecuteCommandParams(n8nParams: Record<string, unknown>): TransformParamResult {
  return {
    parameters: {
      jsCode: `// Shell command converted to code node\n// Original command: ${n8nParams.command}\nthrow new Error('Shell commands not supported - please rewrite as JavaScript');`,
      _originalCommand: n8nParams.command,
    },
    unsupportedParams: [{
      name: 'shellCommand',
      value: n8nParams.command,
      reason: 'Shell commands not supported in EWA - rewrite as JavaScript',
    }],
  };
}
